import { useCallback, useMemo } from 'react';
import clsx from 'clsx';
import { Button } from '@marriott/mi-ui-library';
import { useRouter } from 'next/router';
import { useStore } from '../../modules/store/cobrandStore';
import { CtaComponentTypes } from '../../molecules/CtaComponent/CtaComponent.types';
import {
  buildCompareClickTrackValue,
  buildSelectedCardsCompareUrl,
  persistCompareSelections,
  toBoolean,
} from '../../modules/utils';

type ComparisonTrayCompareButtonProps = Pick<
  CtaComponentTypes,
  'openInNewTab' | 'targetExperience' | 'trackingProperties'
> & {
  compareLinkUrl?: string;
  disabled?: boolean;
  compareLinkText?: string;
};

export const ComparisonTrayCompareButton = ({
  compareLinkUrl,
  openInNewTab,
  targetExperience,
  trackingProperties,
  disabled = false,
  compareLinkText,
}: ComparisonTrayCompareButtonProps) => {
  const router = useRouter();
  const { cobrandData } = useStore(store => store);

  const isExternalLink = toBoolean(openInNewTab);
  const linkType = isExternalLink ? 'external' : 'internal';
  const linkTarget = isExternalLink ? '_blank' : '_self';

  const compareButtonUrl = useMemo(
    () =>
      buildSelectedCardsCompareUrl({
        baseUrl: compareLinkUrl ?? '',
        selectedCards: cobrandData.selectedCards,
        routerQuery: router?.query,
        targetExperience,
      }),
    [cobrandData.selectedCards, compareLinkUrl, router?.query, targetExperience]
  );

  const clickTrackValue = useMemo(
    () => buildCompareClickTrackValue({ linkType, trackingProperties }),
    [linkType, trackingProperties]
  );

  const handleCompareClick = useCallback(() => {
    persistCompareSelections({
      cobrandData,
      currentPath: router?.asPath,
    });
  }, [cobrandData, router?.asPath]);

  const isButtonDisabled = disabled || !compareLinkUrl;

  return (
    <Button
      className={clsx('m-button-l compare-button mx-auto px-4 text-center', isButtonDisabled && 'disabled')}
      isLink
      href={isButtonDisabled ? undefined : compareButtonUrl}
      target={linkTarget}
      linkType={linkType}
      isDisabled={isButtonDisabled}
      trackingProperties={trackingProperties}
      callback={handleCompareClick}
      testId="comparison-tray-compare-button"
      custom_click_track_value={clickTrackValue}
      buttonCopy={compareLinkText ?? 'Compare'}
    />
  );
};
